import React, { useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { getAllAdminUsers, getFarmVerifications } from "services";

import { formatNumber, getAdminVerificationYearlyDataYear, useAppType, useGetAllCarbonPrograms, useNavigate } from "utils";

import { DEFAULT_CACHE, DEFAULT_SHORT_CACHE, GET_ADMIN_USERS, GET_FARM_VERIFICATIONS } from "consts";

import {
  AdminHead,
  Col,
  ErrorBoundary,
  FilterSet,
  Flex,
  Icon,
  InputSearch,
  MainPage,
  Row,
  Switch,
  Table,
  TablePanel,
  Tag,
  Tooltip,
  Typography,
} from "components";

import { SelectAdminContextMenu } from "pages/admin/verifications/components/misc/farmList/SelectAdminContextMenu";
import { TableSubPanels } from "pages/admin/verifications/components/misc/farmList/TableSubPanels";
import { VerifyButton } from "pages/admin/verifications/components/misc/farmList/VerifyButton";

type QueryFilter = string[];

export const FarmList = () => {
  const [expandedRows, setExpandedRows] = useState<React.Key[]>([]);
  const [filters, setFilters] = useState<QueryFilter>([]);
  const [includeAll, setIncludeAll] = useState(false);
  const [onlySoilPlus3, setOnlySoilPlus3] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [timer, setTimer] = useState<NodeJS.Timeout>();

  const defaultItemsPerPage = 20;
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

  const appType = useAppType();
  const navigate = useNavigate();

  const verificationYear = getAdminVerificationYearlyDataYear();

  const updateSearch = (s: string) => {
    clearTimeout(timer);
    setTimer(
      setTimeout(() => {
        setSearch(s.length ? s : "");
      }, 1000)
    );
  };

  const { data, isLoading } = useQuery({
    queryKey: [GET_FARM_VERIFICATIONS, filters[0], filters[1], search, page, itemsPerPage, includeAll, onlySoilPlus3],
    queryFn: () =>
      getFarmVerifications({
        filter: filters[0] ?? undefined,
        assigneeId: filters[0] === "withAssignee" ? filters[1] : undefined,
        carbonProgramId: filters[0] === "carbonProgram" ? filters[1] : undefined,
        search,
        page: page - 1,
        itemsPerPage,
        includeAll,
        onlySoilPlus3,
      }),
    // Short cache so we don't use yesterday evening's results the following morning
    ...DEFAULT_SHORT_CACHE,
  });

  const { data: adminUsers } = useQuery({
    queryKey: [GET_ADMIN_USERS, "verifier"],
    queryFn: () => getAllAdminUsers({ verifier: true }),
    ...DEFAULT_CACHE,
  });

  const { data: carbonPrograms } = useGetAllCarbonPrograms();

  const handlePageChange = (page: number, pageSize: number) => {
    setPage(page);
    setItemsPerPage(pageSize);
  };

  const handleSelectFilter = (key: string) => {
    setFilters([...filters, key]);
    setPage(1);
  };

  const handleUnselectFilter = (i: number) => {
    setFilters(i === 0 ? [] : [filters[0]]);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters([]);
    setPage(1);
  };

  const filterSettings = {
    selectedFilters: [...filters],
    onClearFilters: handleClearFilters,
    onSelectFilter: handleSelectFilter,
    onUnselectFilter: handleUnselectFilter,
    filters: {
      withAssignee: {
        title: "with assignee",
        // Convert admin users array into object
        filters: Object.fromEntries(adminUsers?.map(({ id, name }) => [id, { title: name }]) ?? []),
      },
      carbonProgram: {
        title: "carbon program",
        // Convert carbon programs array into object
        filters: Object.fromEntries(carbonPrograms?.map(({ id, programName }) => [id, { title: programName }]) ?? []),
      },
    },
  };

  const tableData = data?.farms.map((farm) => {
    const { id, name, reviewerId, fieldVerificationsCounts, practiceVerificationsCounts } = farm;
    const reviewer = adminUsers?.find((admin) => admin.id === reviewerId);

    // Calculate practice verification totals
    const practiceStats = Object.values(practiceVerificationsCounts || {}).reduce(
      (acc, counts) => ({
        verified: acc.verified + (counts?.verified ?? 0),
        rejected: acc.rejected + (counts?.rejected ?? 0),
        pending: acc.pending + (counts?.pending ?? 0),
      }),
      { verified: 0, rejected: 0, pending: 0 }
    );
    const hasPendingPractices = practiceStats.pending > 0;
    const practiceVerifiedCount = practiceStats.verified + practiceStats.rejected;
    const practiceTotalCount = practiceStats.verified + practiceStats.rejected + practiceStats.pending;

    // Progress: verified items / total items (farm + fields + practices)
    let verifiedCount = fieldVerificationsCounts.rejectedFieldCount + fieldVerificationsCounts.verifiedFieldCount + practiceVerifiedCount;
    if (farm.verified !== null) {
      verifiedCount += 1; // Farm is verified
    }

    // Total items: 1 (farm) + fields + practices
    const totalCount =
      1 +
      fieldVerificationsCounts.verifiedFieldCount +
      fieldVerificationsCounts.rejectedFieldCount +
      fieldVerificationsCounts.unverifiedFieldCount +
      practiceTotalCount;

    let tag = null;

    if (farm.taskStatus === "pending_verifications" || (farm.taskStatus === "no_tasks" && farm.verified)) {
      tag = <Tag colour="yellow" text="pending verification" />;
    }

    if (farm.taskStatus === "missing_documents") {
      tag = <Tag colour="red" text="missing documentation" />;
    }

    const farmVerifications = {
      color: "blue",
      text: "ready to approve",
      icon: "tabler/alert-triangle",
    };

    // Check for pending verifications: farm, fields, or practices
    if (farm.verified === null || fieldVerificationsCounts.unverifiedFieldCount > 0 || hasPendingPractices) {
      farmVerifications.color = "grey-600";
      farmVerifications.text = "not verified";
      farmVerifications.icon = "tabler/help";
    }

    const currentVerificationYearApproval = farm.payoutApprovals?.find(({ year }) => year === verificationYear);

    if (currentVerificationYearApproval) {
      const { approved } = currentVerificationYearApproval;

      tag = null;
      farmVerifications.color = approved ? "green" : "red";
      farmVerifications.text = approved ? "approved" : "rejected";
      farmVerifications.icon = approved ? "tabler/circle-check" : "tabler/circle-x";
    }

    // Calculate credits from points (credits = points / 1000)
    const pointsSum = (farm.points?.removals || 0) + (farm.points?.avoidances || 0) + (farm.points?.reductions || 0);
    const credits = pointsSum / 1000;

    // Calculate initial credits (at verification start)
    const pointsInitialSum = (farm.pointsInitial?.removals || 0) + (farm.pointsInitial?.avoidances || 0) + (farm.pointsInitial?.reductions || 0);
    const creditsInitial = pointsInitialSum / 1000;

    // Check if there's a difference between initial and current
    const hasCreditsChange = Math.abs(creditsInitial - credits) > 0.01;

    // Check if farm has new soil plus 3 data, if yes we want to add that as additional tag
    if (farm.hasSoilPlus3Data) {
      tag = (
        <>
          {tag}
          <Tag text="soil+ 3.0" />
        </>
      );
    }

    return {
      key: id,
      // @ts-expect-error TablePanel not yet in TS
      "farm name": <TablePanel className="farms-panel" headline={name} subtitle={farm.carbonProgramName} tag={tag} />,
      size: <Typography variant="paragraph-xs">{farm.size}</Typography>,
      progress: (
        <div>
          {verifiedCount}/{totalCount}
        </div>
      ),
      status: (
        <div className={farmVerifications.color}>
          <Icon iconName={farmVerifications.icon} className="icon inline margin-right-25" />
          <Typography variant="paragraph-m">{farmVerifications.text}</Typography>
        </div>
      ),
      "credit generation": (
        <div>
          <span className="paragraph-xs-bold">{formatNumber({ number: credits, decimalPlaces: 2 })}</span>
          {hasCreditsChange && <span className="paragraph-xs grey-text"> / {formatNumber({ number: creditsInitial, decimalPlaces: 2 })}</span>}
        </div>
      ),
      admin: <SelectAdminContextMenu reviewer={reviewer} adminUsers={adminUsers ?? []} reviewerId={reviewerId} farmId={farm.id} />,
      expandable: (
        <ErrorBoundary>
          <div className="margin-top-200">
            <TableSubPanels farm={farm} />
          </div>
          <VerifyButton farm={farm} />
        </ErrorBoundary>
      ),
      " ": <Icon className="icon inline" iconName={`tabler/chevron-${expandedRows.includes(id) ? "up" : "down"}`} />,
    };
  });

  return (
    <MainPage loading={isLoading} pageName={"admin"}>
      <AdminHead
        headline="Verifications"
        extra={
          <Tooltip placement="left" title="Verification task settings">
            <Icon iconName="tabler/settings" className="icon inline pointer" size="m" onClick={() => navigate("/verifications/tasks")} />
          </Tooltip>
        }
      />
      <div className="verification-farms">
        <Flex justify="space-between" align="center" className="margin-bottom-100">
          <Typography variant="h3">{data?.count} farms</Typography>
          <Flex gap={16} align="center">
            <Flex vertical align="center">
              <span className="paragraph-xs grey-text">At Verification Start</span>
              <span className="h3 grey-text">
                {formatNumber({
                  number: data?.totalPointsInitial
                    ? (data.totalPointsInitial.removals + data.totalPointsInitial.avoidances + data.totalPointsInitial.reductions) / 1000
                    : 0,
                  decimalPlaces: 2,
                })}
              </span>
            </Flex>
            <Icon iconName="tabler/arrow-right" className="icon inline grey-text" />
            <Flex vertical align="center">
              <span className="paragraph-xs grey-text">Latest</span>
              <span className="h3">
                {formatNumber({
                  number: data?.totalPoints ? (data.totalPoints.removals + data.totalPoints.avoidances + data.totalPoints.reductions) / 1000 : 0,
                  decimalPlaces: 2,
                })}
              </span>
            </Flex>
          </Flex>
        </Flex>
        <Row>
          <Col>
            <Flex className="padding-100" vertical gap={16}>
              <InputSearch label={"farms search"} onChange={(e) => updateSearch(e.target.value)} />
              <Flex gap={16}>
                Include all farms
                <Switch value={includeAll} onChange={(v) => setIncludeAll(v)} />
                Only Soil Plus 3.0
                <Switch value={onlySoilPlus3} onChange={(v) => setOnlySoilPlus3(v)} />
              </Flex>

              <FilterSet {...filterSettings} />
            </Flex>
            <Row>
              <Col>
                {!isLoading && data?.count === 0 && <Typography variant="paragraph-m">There are no farms to verify.</Typography>}
                <Table
                  data={tableData ?? []}
                  className={"farms"}
                  pagination={{
                    total: data?.count,
                    defaultPageSize: defaultItemsPerPage,
                    pageSize: itemsPerPage,
                    onChange: handlePageChange,
                  }}
                  loading={isLoading}
                  expandable={{
                    onExpandedRowsChange: (ks) => setExpandedRows([...ks]),
                    expandedRowRender: (item) => <>{item.expandable}</>,
                  }}
                  hideHeader={appType.mobile}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </MainPage>
  );
};
